import React, { useRef, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './App.css';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
    useTheme,
    useMediaQuery,
    IconButton
} from '@mui/material';
import LaptopIcon from '@mui/icons-material/Laptop';
import Joyride from 'react-joyride'

import customTheme from './theme';
import { useAbortController } from './contexts/AbortControllerContext';
import ProjectContext from './contexts/ProjectContext';
import SupportContext from './contexts/SupportContext';
import UserContext from './contexts/UserContext';
import { FormProvider } from './contexts/FormContext';

import PrimarySearchAppBar from './components/structure/PrimarySearchAppBar';
import Sidebar from './components/structure/Sidebar';
import {useFetch} from './components/auth/useFetch';

import GreenButton from './components/buttons/GreenButton';
import SupportDialog from './components/dialog/SupportDialog/SupportDialog';

import ScrollbarStyle from './ScrollbarStyle';
import BaseWindow from './components/windows/BaseWindow';

import KonsolRoutes from './components/routes/router';

import { KonsolScopes } from './components/auth/scopes';

import ProjectSelectionView from './views/ProjectSelectionView';
import LoadingView from './views/LoadingView';
import LoginView from './components/auth/LoginView';
import EULADialog from './components/dialog/EULADialog';
import NotFoundView from './components/auth/NotFoundView';
import WarnMobileFeaturesDialog from './components/dialog/WarnMobileFeaturesDialog';
import { Support } from '@mui/icons-material';
import { type } from '@testing-library/user-event/dist/type';


function App(props) {
    const websocketRef = useRef(null)
    const parentRef = useRef(null);
    const location = useLocation();
    const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
    const { abortAllRequests, abortGroupRequests, registerController, unregisterController } = useAbortController()
    const { setPermissions } = props;
    const [isLoading, setIsLoading] = useState(false);
    const fetchWithAuth = useFetch()

    const [transferdate, setTransferdate] = useState('')
    const [transferDates, setTransferDates] = useState([])
    const [linkSummary, setLinkSummary] = useState({})
    const [openEULA, setOpenEULA] = useState(null)
    const [userInProduction, setUserInProduction] = useState(true)
    const [konsolUser, setKonsolUser] = useState(null)
    const [konsolUserEmail, setKonsolUserEmail] = useState(null)

    const [isLocked, setIsLocked] = useState(false);
    const [isOffscreen, setIsOffscreen] = useState(true);
    const [isFaded, setIsFaded] = useState(true);
    const [highlighted, setHighlighted] = useState(false);

    const [supportDialogOpen, setSupportDialogOpen] = useState(false)

    const [tourSteps, setTourSteps] = useState([])
    const [runTour, setRunTour] = useState(false)

    // Project variables
    const [projects, setProjects] = useState([])
    const [selectedProject, setSelectedProject] = useState({})

    // Sidebar
    const [dailies, setDailies] = useState(false)
    const [illusion, setIllusion] = useState(false)
    const [assets, setAssets] = useState(false)
    const [project, setProject] = useState(false)
    const [settings, setSettings] = useState(false)

    // Mobile features warning
    const [openMobileWarn, setOpenMobileWarn] = useState(false)
    const [mobileWarnAnchorEl, setMobileWarnAnchorEl] = useState()

    // Data load variables
    const [dataLoaded, setDataLoaded] = useState(false)
    
    const makeTour = (items) => {
        setTourSteps(items)
    }

    const getLinkSummary = (transferDate) => {
        abortGroupRequests(`${transferdate}.get.linksummary`)
        const linkSummaryController = registerController(`${transferdate}.get.linksummary`)
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/linksummary/?transfer_date=${transferDate}`, {
            headers: {
                'X-Project-ID': selectedProject?.project_db
            },
            signal: linkSummaryController.signal
        })
            .then((res) => res.json())
            .then(result => {
                setLinkSummary(result)
            })
            .catch(e => {
                if(e.name !== 'AbortError'){
                    console.error(e);
                }
            })
            .finally(() => unregisterController(linkSummaryController, `${transferdate}.get.linksummary`))
    }

    const startTour = () => {
        if (location.pathname === '/about' && !isOffscreen){
            setIsFaded(true);
            setIsOffscreen(true);
            setTimeout(() => {
                setRunTour(true)
            }, 900)
        } else {
            setRunTour(true)
        }
    }

    const endTour = () => {
        setRunTour(false)
    }
    
    const handleToggle = () => {
        setIsOffscreen((prev) => !prev);
    };

    const autoToggle = () => {
        if (!isLocked) {
            setIsFaded(true);
            setIsOffscreen(true);
        }
    }

    useEffect(() => {
        document.body.classList.add('no-scroll');
        isAuthenticated && (async () => {
            try {
                getDatabases()
            } catch (e) {
                console.error(e)
            } 
        })();
    }, [isAuthenticated])


    useEffect(() => {
        isAuthenticated && (async () => {
            try {
                // If the user is in the MongoDB for this project, set their 'activated' flag to True
                // To show the user's account has usage on it
                const login = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/user/login`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Project-ID': selectedProject?.project_db
                    },
                })
                if (!login.ok) {
                    throw new Error('Failed to log in user');
                }
                await getTransferDates()
                setDataLoaded(true)

            } catch (e) {
                console.error(e);
            }
        })();
    }, [selectedProject?.project_db])


    useEffect(() => {
        (async () => {
            if (isAuthenticated && user) {
                setKonsolUser(user)
                setPermissions(user?.project_permissions ?? [])
                setKonsolUserEmail(await getUserEmail())
            }
        })();
    }, [user, isAuthenticated])


    const getUserEmail = async () => {
        let res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/user/email`, {
            headers: {
                'X-Project-ID': "Konsol"
            },
        });
        let res_json = await res.json();  
        let email = res_json?.email;
        return email;
    }

    // If you delete this Konsol will fail to load after EULA accept button clicked!
    //  On Eula window close, call getDatabases()
    useEffect(() => {
        if(openEULA === false){
            getDatabases()   
        }
    }, [openEULA])

    useEffect(() => {
        if(konsolUser?.user_metadata?.sidebarlock !== undefined){
            const sidebarLocked = konsolUser?.user_metadata?.sidebarlock
            setIsLocked(sidebarLocked)
            setIsOffscreen(!sidebarLocked)
            setIsFaded(!sidebarLocked)
        }
    }, [konsolUser?.user_metadata?.sidebarlock])


    const closeEulaDialog = () => {
        setOpenEULA(false)
        user.app_metadata.eula = true
    }

    const handleProjectSwitch = async(project) => {
        abortAllRequests()
        setDataLoaded(false)
        setSelectedProject(project)
        setTransferDates([])
        getAccessTokenSilently({
            cacheMode: 'off',
            detailedResponse: true,
            authorizationParams: {
                project: project?.project_db ?? null,
            }
        })
    }

    const landingScreenLoaded = () => {
        setTimeout(() => {
            setDataLoaded(true)
        }, 2500);
    }

    const supportClick = () => {
        setSupportDialogOpen(true)
    }



    const getDatabases = () => {
        if (isAuthenticated){
            try {
                    fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/databases/`, {
                        headers:{
                            'X-Project-ID': 'Konsol'
                        }
                    })
                    .then((res) => res.json())
                    .then(result => {
                        setProjects(result)
                        if(result.length === 1){
                            handleProjectSwitch(result[0])
                        }
                        else{
                            landingScreenLoaded()
                        }
                    })
            } catch (e) {
                throw(e)
            }
        }
    }

    const getTransferDates = async () => {
        const transferDatesController = registerController(``)
        try{
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/transferdates/`, {
                headers: {
                    'X-Project-ID': selectedProject?.project_db
                },
                signal: transferDatesController.signal
            })
            if(!response.ok){
                throw new Error(`Failed to fetch transfer dates`);
            }
            const transferDates = await response.json()
            const transferDate = await transferDates.at(0) ?? ''
            setTransferDates(transferDates)
            setTransferdate(transferDate)
            getLinkSummary(transferDate)

        }
        catch (e) {
            if(e.name !== 'AbortError'){
                console.error(e);
            }
            setUserInProduction(false)
        }
        finally {
            unregisterController(transferDatesController)
        }
    }



    const handleCallback = (data) => {
        if (data.step.page === 'About'){
            if (data.index === 1){
                setIsLoading(true)
            } 
            else if (data.index === 2){
                setIsLoading(false)
                if (isOffscreen){
                    setIsFaded(false)
                    setIsOffscreen(false)
                }
            }
            else if (data.index == 3){
                setHighlighted(true)
            }
            else if (data.index === 4){
                setHighlighted(false)
            }
            else if (data.index === 5){
            }
            else if (data.index === 8){
                if (data.action === 'update'){
                    setTimeout(() => {
                        setDailies(true);
                        setTimeout(() => {
                            setIllusion(true);
                            setTimeout(() => {
                                setAssets(true);
                                setTimeout(() => {
                                    setDailies(false);
                                    setTimeout(() => {
                                        setIllusion(false);
                                    }, 2200);
                                }, 1900);
                            }, 2300);
                        }, 1900);
                    }, 1400);
                }
            }
        }
        if (data.type === 'tour:end'){
            setDailies(false)
            setIllusion(false)
            setAssets(false)
            endTour()
        }
    };
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'))
    const isSm = useMediaQuery(theme.breakpoints.down('md'))
    const isMd = useMediaQuery(theme.breakpoints.down('lg'))
    const isLg = useMediaQuery(theme.breakpoints.down('xl'))
    
    return (
        <ThemeProvider theme={customTheme}>
            {isXs || isSm?
                <IconButton
                    onClick={(event) => {
                        setOpenMobileWarn(true)
                        setMobileWarnAnchorEl(event.currentTarget)
                    }}
                    sx={{
                        backgroundColor: 'rgba(200,120,0,0.9)',
                        color: 'white',
                        position: 'absolute',
                        bottom: '20px',
                        left: '20px',
                        zIndex: '1500',
                        '&:hover': {
                            backgroundColor: 'rgba(200,120,0,0.9)', // Ensures it remains white on hover
                        },
                        '&:active': {
                            backgroundColor: 'rgba(200,120,0,0.9)', // Ensures it remains white on click
                        }
                    }}
                >
                    <LaptopIcon/>
                </IconButton>
                :
                <></>
            }
            <WarnMobileFeaturesDialog 
                open={openMobileWarn}
                setOpen={setOpenMobileWarn}
                anchorEl={mobileWarnAnchorEl}
            />
            <ScrollbarStyle />
            {
                isAuthenticated // User has been authenticated
                ?
                    // user?.app_metadata?.eula // User has accepted EULA
                    // ?
                    <div className="App">
                        {
                            selectedProject?.project_db // Project DB not falsey
                            ?
                            <ProjectContext.Provider value={selectedProject}>
                            {
                                userInProduction // User is in production then load Konsol with the production
                                ?
                                <>
                                {
                                    dataLoaded
                                    ?
                                    <>
                                    <Joyride
                                        callback={handleCallback}
                                        steps={tourSteps}
                                        continuous
                                        run={runTour}
                                        styles={{
                                            options: {
                                                arrowColor: 'rgba(255,255,255,0.5)',
                                                beaconSize: 40,
                                                backgroundColor: 'rgba(17,17,17,1)',
                                                textColor: '#ffffff',
                                                primaryColor: '#ff5733',
                                            },
                                            tooltip: {
                                                border: '2px solid rgba(255,255,255,0.6)',
                                                borderRadius: '8px',
                                                fontFamily: 'Roboto, sans-serif',
                                            },
                                            tooltipTitle: {
                                                fontFamily: 'Roboto, sans-serif', 
                                            },
                                            tooltipContent: {
                                                textAlign: 'left', 
                                                fontFamily: 'Roboto, sans-serif',
                                            },
                                            buttonNext: {
                                                backgroundColor: 'rgb(71 132 96)',
                                                width: '100%',
                                                fontWeight: 'bold',
                                            },
                                            buttonBack: {
                                                display: 'none', 
                                            },
                                            buttonClose: {
                                                display: 'none', 
                                            },
                                            buttonSkip: {
                                                display: 'none', 
                                            },
                                            spotlight: {
                                                backgroundColor: 'rgba(255, 255, 255, 0.4)', 
                                                borderRadius: '10px',
                                                boxShadow: '0 0 20px rgba(176, 236, 175, 0.5)',
                                            }
                                        }}
                                    />
                                    <div
                                        style={{ 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            height: '100vh',
                                        }}
                                        onContextMenu={(e) => e.preventDefault()}
                                    >
                                        <UserContext.Provider value={{ konsolUser, setKonsolUser, konsolUserEmail }}>
                                            <SupportContext.Provider value={{ supportDialogOpen, setSupportDialogOpen }}>
                                                <FormProvider>
                                                    <PrimarySearchAppBar
                                                        handleToggle={handleToggle}
                                                        transferdate={transferdate}
                                                        setTransferdate={setTransferdate}
                                                        transferDates={transferDates}
                                                        makeTour={makeTour}
                                                        startTour={startTour}
                                                        isFaded={isFaded}
                                                        setIsFaded={setIsFaded}
                                                        isLocked={isLocked}
                                                        setIsLocked={setIsLocked}
                                                        isLoading={isLoading}
                                                        linkSummary={linkSummary}
                                                        getLinkSummary={getLinkSummary}
                                                        getTransferDates={getTransferDates}
                                                        websocketRef={websocketRef}
                                                    />
                                                    <div 
                                                        style={{ 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between', 
                                                            // Appbar is 64px and placed above the content
                                                            height: `calc(100% - 64px)` ,
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <Sidebar 
                                                            isOffscreen={isOffscreen} 
                                                            autoToggle={autoToggle} 
                                                            projects={projects} 
                                                            dailies={dailies} setDailies={setDailies}
                                                            illusion={illusion} setIllusion={setIllusion}
                                                            assets={assets} setAssets={setAssets}
                                                            project={project} setProject={setProject}
                                                            settings={settings} setSettings={setSettings}
                                                            setSelectedProject={handleProjectSwitch}
                                                            />
                                                        {/* If in Xs (mobile) view, sidebar should take up whole screen. Otherwise, share screen with BaseWindow */}
                                                        {
                                                            isXs && !isOffscreen 
                                                            ?
                                                            <></>
                                                            :
                                                            <BaseWindow parentRef={parentRef} >
                                                                <KonsolRoutes
                                                                    getLinkSummary={getLinkSummary}
                                                                    transferdate={transferdate}
                                                                    getTransferDates={getTransferDates}
                                                                    makeTour={makeTour} 
                                                                    isLoading={isLoading} 
                                                                    setIsLoading={setIsLoading} />
                                                            </BaseWindow>
                                                        }
                                                    </div>
                                                    <SupportDialog
                                                        open={supportDialogOpen}
                                                        setOpen={setSupportDialogOpen}
                                                        locationPathname={location.pathname}
                                                    />
                                                </FormProvider>                                                
                                            </SupportContext.Provider>
                                        </UserContext.Provider>
                                    </div>
                                    </>
                                    :
                                    <LoadingView selectedProject={selectedProject} />
                                }
                                </>
                                : // User not in requested production. Explain what to do
                                <NotFoundView />
                            }
                            </ProjectContext.Provider>
                            : // Project hasn't been selected. Present the option to select from available projects
                            <ProjectSelectionView
                                dataLoaded={dataLoaded}
                                onProjectSwitch={handleProjectSwitch}
                                projects={projects}
                            />
                        }   
                    </div>
                    // : // User has not accepted EULA
                    // <EULADialog handleClose={closeEulaDialog}/>
                : // User has not been authenticated
                <LoginView />
            }
        </ThemeProvider>
    );
}

export default App;
